import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import {
  ConfirmDeleteModal,
  BatchActionPill,
  useBatchSelection,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useSectionsList } from "./api/useSectionsList";
import { useQuestionsList } from "./api/useQuestionsList";
import { useSectionMutations } from "./api/useSectionMutations";
import { useQuestionMutations } from "./api/useQuestionMutations";
import { SectionCard } from "./SectionCard";
import { SectionModal } from "./SectionModal";
import { QuestionSlideout } from "./QuestionSlideout";
import { reorderSections, applyQuestionDrag } from "./reorder";
import {
  emptyQuestionForm,
  emptySectionForm,
  type Question,
  type QuestionFormValues,
  type Section,
  type SectionFormValues,
} from "./types";
import "./style.css";

export interface QuestionsTabProps {
  questionnaireId: string;
}

const toFormValues = (q: Question): QuestionFormValues => ({
  question: q.question,
  name: q.name,
  valueType: q.valueType,
  required: q.required,
  settings: q.settings,
  selectOptions: q.selectOptions,
});

// Sortable wrapper — lives above QuestionsTab to avoid re-declaring on every
// render, but is only used within QuestionsTab's JSX.
interface SortableSectionProps {
  section: Section;
  questions: Question[];
  onRename: (s: Section) => void;
  onDelete: (s: Section) => void;
  onAddQuestion: (s: Section) => void;
  onEditQuestion: (q: Question) => void;
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
}

const SortableSection = (props: SortableSectionProps): JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: `section:${props.section.id}` });
  return (
    <SectionCard
      {...props}
      draggable
      setNodeRef={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      dragHandleProps={{ ...attributes, ...listeners }}
    />
  );
};

export const QuestionsTab = ({
  questionnaireId,
}: QuestionsTabProps): JSX.Element => {
  const sections = useSectionsList(questionnaireId);
  const questions = useQuestionsList(questionnaireId);
  const sectionMut = useSectionMutations(questionnaireId);
  const questionMut = useQuestionMutations(questionnaireId);

  // Optimistic local copies — synced from server state when a fetch completes,
  // mutated on drag-end for instant feedback, then reconciled by refetch.
  // We initialise directly from rows so the first render is immediate, then
  // re-sync whenever the fetching state settles (isFetching transitions to
  // false after a refetch). Using a ref for rows inside the effect avoids
  // reacting to the reference identity of the returned array on every render.
  const sectionRowsRef = useRef(sections.rows);
  sectionRowsRef.current = sections.rows;
  const questionRowsRef = useRef(questions.rows);
  questionRowsRef.current = questions.rows;

  const [localSections, setLocalSections] = useState<Section[]>(
    () => sections.rows
  );
  const [localQuestions, setLocalQuestions] = useState<Question[]>(
    () => questions.rows
  );

  useEffect(() => {
    if (!sections.isFetching) setLocalSections(sectionRowsRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections.isFetching]);

  useEffect(() => {
    if (!questions.isFetching) setLocalQuestions(questionRowsRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions.isFetching]);

  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const [sectionModal, setSectionModal] = useState<{
    mode: "create" | "edit";
    section?: Section;
  } | null>(null);

  const [slideout, setSlideout] = useState<{
    mode: "create" | "edit";
    sectionId: string;
    question?: Question;
  } | null>(null);

  const [deleteSection, setDeleteSection] = useState<Section | null>(null);

  // Tab-wide multi-select of questions for batch delete.
  const sel = useBatchSelection();
  const [confirmBatchDelete, setConfirmBatchDelete] = useState(false);

  // Derive render-order from local state so optimistic updates are instant.
  const questionsBySection = useMemo(() => {
    const map: Record<string, Question[]> = {};
    localQuestions.forEach((q) => {
      (map[q.sectionId] ??= []).push(q);
    });
    Object.values(map).forEach((list) =>
      list.sort((a, b) => a.position - b.position)
    );
    return map;
  }, [localQuestions]);

  const orderedSections = useMemo(
    () => [...localSections].sort((a, b) => a.position - b.position),
    [localSections]
  );

  const refetchAll = () => {
    sections.refetch();
    questions.refetch();
  };

  // dnd-kit sensors — require 4 px of movement to start a drag so clicks fire
  // normally on buttons inside the cards.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const [type, id] = String(active.id).split(":");
    const [overType, overId] = String(over.id).split(":");

    if (type === "section" && overType === "section") {
      const { next, changed } = reorderSections(localSections, id, overId);
      if (!changed.length) return;
      setLocalSections(next);
      try {
        for (const s of changed) await sectionMut.reorder(s.id, s.position);
      } catch {
        setToast({ variant: "danger", text: "Could not save section order." });
      }
      refetchAll();
      return;
    }

    if (type === "question") {
      const { next, changed } = applyQuestionDrag(
        localQuestions,
        id,
        overId,
        overType === "section"
      );
      if (!changed.length) return;
      setLocalQuestions(next);
      try {
        for (const q of changed)
          await questionMut.reorder(q.id, q.position, q.sectionId);
      } catch {
        setToast({ variant: "danger", text: "Could not save question order." });
      }
      questions.refetch();
    }
  };

  const saveSection = async (values: SectionFormValues) => {
    try {
      if (sectionModal?.mode === "edit" && sectionModal.section) {
        await sectionMut.update(sectionModal.section.id, values);
      } else {
        await sectionMut.create(values);
      }
      setSectionModal(null);
      refetchAll();
    } catch {
      setToast({ variant: "danger", text: "Could not save section." });
    }
  };

  const saveQuestion = async (values: QuestionFormValues) => {
    if (!slideout) return;
    try {
      if (slideout.mode === "edit" && slideout.question) {
        await questionMut.update(
          slideout.question.id,
          values,
          slideout.sectionId
        );
      } else {
        await questionMut.create(values, slideout.sectionId);
      }
      setSlideout(null);
      questions.refetch();
    } catch {
      setToast({ variant: "danger", text: "Could not save question." });
    }
  };

  const confirmDeleteSection = async () => {
    if (!deleteSection) return;
    try {
      await sectionMut.remove(deleteSection.id);
      setDeleteSection(null);
      refetchAll();
    } catch {
      setToast({ variant: "danger", text: "Could not delete section." });
    }
  };

  const confirmBatchDeleteQuestions = async () => {
    setConfirmBatchDelete(false);
    const ids = [...sel.selectedIds];
    let failures = 0;
    for (const id of ids) {
      try {
        await questionMut.remove(id);
      } catch {
        failures += 1;
      }
    }
    if (failures > 0) {
      setToast({
        variant: "danger",
        text: `Failed to delete ${failures} of ${ids.length} question(s).`,
      });
    }
    sel.clear();
    questions.refetch();
  };

  const batchActions = [
    {
      icon: faTrashCan,
      label: "Delete",
      onClick: () => setConfirmBatchDelete(true),
      variant: "danger" as const,
    },
  ];

  if (sections.isLoading || questions.isLoading) return <ScopeStackSpinner />;

  return (
    <div className="questions-tab-card">
      <div className="questions-tab-card__header">
        <h2 className="questions-tab-card__title">Survey Questions</h2>
        <div className="questions-tab-card__actions">
          <BatchActionPill
            selectedCount={sel.selectedIds.length}
            actions={batchActions}
          />
          <Button
            className="squareGreenButton"
            onClick={() => setSectionModal({ mode: "create" })}
          >
            Add Survey Section
          </Button>
        </div>
      </div>

      {orderedSections.length === 0 ? (
        <div className="text-muted p-3">
          Add a section to start adding questions.
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderedSections.map((s) => `section:${s.id}`)}
            strategy={verticalListSortingStrategy}
          >
            {orderedSections.map((section) => (
              <SortableSection
                key={section.id}
                section={section}
                questions={questionsBySection[section.id] ?? []}
                onRename={(s) => setSectionModal({ mode: "edit", section: s })}
                onDelete={(s) => setDeleteSection(s)}
                onAddQuestion={(s) =>
                  setSlideout({ mode: "create", sectionId: s.id })
                }
                onEditQuestion={(q) =>
                  setSlideout({
                    mode: "edit",
                    sectionId: q.sectionId,
                    question: q,
                  })
                }
                selectedIds={sel.selectedIds}
                setSelectedIds={sel.setSelectedIds}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}

      {sectionModal && (
        <SectionModal
          isOpen
          mode={sectionModal.mode}
          initialValues={sectionModal.section ?? emptySectionForm}
          onClose={() => setSectionModal(null)}
          onSave={saveSection}
        />
      )}

      <QuestionSlideout
        isOpen={!!slideout}
        mode={slideout?.mode ?? "create"}
        initialValues={
          slideout?.question
            ? toFormValues(slideout.question)
            : emptyQuestionForm
        }
        slug={slideout?.question?.slug ?? ""}
        onClose={() => setSlideout(null)}
        onSave={saveQuestion}
      />

      <ConfirmDeleteModal
        open={!!deleteSection}
        title="Delete section?"
        message={`Delete "${
          deleteSection?.name ?? ""
        }"? Its questions may be reassigned or removed.`}
        confirmLabel="Delete"
        onConfirm={confirmDeleteSection}
        onCancel={() => setDeleteSection(null)}
      />

      <ConfirmDeleteModal
        open={confirmBatchDelete}
        title="Delete questions?"
        message={`Delete ${sel.selectedIds.length} selected question(s)?`}
        confirmLabel="Delete"
        onConfirm={confirmBatchDeleteQuestions}
        onCancel={() => setConfirmBatchDelete(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default QuestionsTab;
